import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PublicFooter from "@/components/PublicFooter";
import { COMPANY, LEGAL_EFFECTIVE_DATE, LEGAL_VERSION } from "@/lib/legalVersion";

// Phase P.18 — GDPR-compliant Privacy Policy for JobsBook.app.
// Copy is drafted to satisfy: GDPR (EU 2016/679), the Irish Data Protection
// Act 2018, and ePrivacy Regulations 2011. Not a substitute for legal
// review by a solicitor, but written to be shippable / lawyer-review quality
// rather than placeholder lorem ipsum.

const sections = [
  {
    n: 1,
    title: "Who we are (Data Controller)",
    body: (
      <>
        <p>
          <strong>{COMPANY.legal_name}</strong> ("we", "us", "our") is the data controller
          for personal data processed via the JobsBook.app service (the "Service"). We are
          a company incorporated in Ireland (CRO no. {COMPANY.cro_number}, VAT{" "}
          {COMPANY.vat_number}) with registered office at {COMPANY.address}. Director:{" "}
          {COMPANY.directors}. You can reach us at{" "}
          <a href={`mailto:${COMPANY.contact_email}`} className="text-accent underline">
            {COMPANY.contact_email}
          </a>{" "}
          for any privacy-related enquiry, including requests to exercise the rights described in
          Section 8 below. The same address also serves as our Data Protection contact — while we
          are not required to appoint a formal Data Protection Officer, this mailbox is monitored
          by a member of staff with GDPR responsibility.
        </p>
        <p className="mt-2">
          When you use JobsBook.app to manage your own customers, <em>you</em> are the controller
          of that customer data and we act as your processor under the Data Processing Addendum
          available at <Link to="/dpa" className="text-accent underline">/dpa</Link>.
        </p>
      </>
    ),
  },
  {
    n: 2,
    title: "The personal data we collect",
    body: (
      <>
        <p>We collect only the data we need to run the Service. Categories:</p>
        <ul className="mt-3 space-y-2 list-disc pl-5 text-foreground/85">
          <li>
            <strong>Account &amp; billing data (managers):</strong> name, business email, hashed
            password, chosen company name, billing country, and Stripe customer/subscription
            identifiers. Card numbers are collected by Stripe and never reach our servers.
          </li>
          <li>
            <strong>Staff data:</strong> staff name, an optional contact email, a hashed 4-digit
            PIN, personal working URL slug and the audit trail of jobs assigned to them.
          </li>
          <li>
            <strong>Customer data (uploaded by our tenants):</strong> company/contact name, phone
            numbers, email addresses, addresses / postcodes / geolocation, booking notes and
            invoice-related identifiers such as VAT number. This data is uploaded and controlled
            by the tenant using JobsBook to serve their own customers.
          </li>
          <li>
            <strong>Operational data:</strong> booking codes, invoice numbers, timestamps, staff
            actions, chat messages between tenant and staff, photos of jobs, customer
            signatures captured on completion.
          </li>
          <li>
            <strong>Technical data:</strong> IP address (truncated to the last 4 octets and stored
            with failed logins only), user agent string, session tokens (JWTs), device timezone.
          </li>
          <li>
            <strong>Communication data:</strong> emails we send on your behalf via Resend
            (booking confirmations, invoices, receipts) — subject line, recipient address, and
            delivery status.
          </li>
        </ul>
      </>
    ),
  },
  {
    n: 3,
    title: "Purposes and lawful bases for processing (Art. 6 GDPR)",
    body: (
      <>
        <table className="w-full text-sm border border-border rounded-md overflow-hidden mt-3">
          <thead className="bg-secondary/40">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold">Purpose</th>
              <th className="px-3 py-2 font-semibold">Lawful basis</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-border">
              <td className="px-3 py-2">Providing the Service (auth, dashboard, invoicing, calendar)</td>
              <td className="px-3 py-2">Art. 6(1)(b) — performance of the contract</td>
            </tr>
            <tr className="border-t border-border">
              <td className="px-3 py-2">Charging your subscription via Stripe</td>
              <td className="px-3 py-2">Art. 6(1)(b) — contract</td>
            </tr>
            <tr className="border-t border-border">
              <td className="px-3 py-2">Sending transactional emails (invoices, receipts) to the tenant's customers</td>
              <td className="px-3 py-2">Art. 6(1)(f) — legitimate interest of the tenant (as their processor) &amp; Art. 6(1)(b)</td>
            </tr>
            <tr className="border-t border-border">
              <td className="px-3 py-2">Preventing fraud, brute-force attacks, abuse</td>
              <td className="px-3 py-2">Art. 6(1)(f) — legitimate interest</td>
            </tr>
            <tr className="border-t border-border">
              <td className="px-3 py-2">Marketing / product-update emails</td>
              <td className="px-3 py-2">Art. 6(1)(a) — consent (opt-in only, withdrawable at any time)</td>
            </tr>
            <tr className="border-t border-border">
              <td className="px-3 py-2">Retaining invoices &amp; ledger for tax records</td>
              <td className="px-3 py-2">Art. 6(1)(c) — legal obligation (Irish Revenue: 7-year retention, s.886 TCA 1997)</td>
            </tr>
          </tbody>
        </table>
        <p className="mt-3">
          We do <strong>not</strong> use your personal data for automated decision-making that
          produces legal or similarly significant effects (Art. 22 GDPR). We do not perform
          profiling for advertising.
        </p>
      </>
    ),
  },
  {
    n: 4,
    title: "Who we share your data with (sub-processors)",
    body: (
      <>
        <p>
          We only share the minimum data required. Current sub-processors are vetted for GDPR
          compliance and bound by written data-protection agreements:
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5 text-foreground/85">
          <li>
            <strong>Stripe Payments Europe, Ltd.</strong> (Dublin, Ireland) — subscription
            billing. Receives your business email, an opaque tenant identifier and
            billing-country. Card data is collected directly by Stripe. Privacy notice:{" "}
            <a className="text-accent underline" href="https://stripe.com/ie/privacy" target="_blank" rel="noopener noreferrer">stripe.com/ie/privacy</a>.
          </li>
          <li>
            <strong>Resend (Resend, Inc., United States)</strong> — transactional email delivery.
            Receives recipient email address, subject line and rendered HTML body of each email
            we send on your behalf. Transfers rely on Standard Contractual Clauses (SCCs, EU
            Commission Decision 2021/914).
          </li>
          <li>
            <strong>Google LLC — Maps &amp; Places APIs</strong> — address auto-complete and map
            rendering. Only the address or coordinates you look up are transmitted. See{" "}
            <a className="text-accent underline" href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a>.
          </li>
          <li>
            <strong>HeiGIT gGmbH — OpenRouteService</strong> (Germany) — driving distance/time
            calculations. Only postcodes / lat-lng coordinates are transmitted; no personal
            identifiers.
          </li>
          <li>
            <strong>MongoDB Atlas (MongoDB Ireland Ltd.)</strong> — primary database hosting in
            an EU region. Data is encrypted at rest (AES-256) and in transit (TLS 1.2+).
          </li>
          <li>
            <strong>OpenAI (via Emergent LLM key gateway)</strong> — optional AI helpers (e.g.
            duplicate-customer detection). Only prompts constructed from anonymised or
            pseudonymised business data are sent. If your account has AI features disabled, no
            data is sent to OpenAI. See <a className="text-accent underline" href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">openai.com/policies/privacy-policy</a>.
          </li>
        </ul>
        <p className="mt-3">
          We never sell personal data and we never share it for third-party advertising. A current
          list of sub-processors is available on request via {COMPANY.contact_email}.
        </p>
      </>
    ),
  },
  {
    n: 5,
    title: "International data transfers",
    body: (
      <>
        <p>
          Where a sub-processor is located outside the European Economic Area (EEA) — primarily
          the United States (Resend, OpenAI) — the transfer is protected by the European
          Commission's Standard Contractual Clauses (SCCs) Module 2 (controller-to-processor) or
          Module 3 (processor-to-processor), supplemented by technical measures (encryption in
          transit, minimum-necessary payloads). Where a sub-processor is certified under the
          EU-US Data Privacy Framework we also rely on that adequacy decision.
        </p>
      </>
    ),
  },
  {
    n: 6,
    title: "How long we keep your data (retention)",
    body: (
      <>
        <ul className="mt-3 space-y-2 list-disc pl-5 text-foreground/85">
          <li>
            <strong>Active account:</strong> retained while your subscription is active.
          </li>
          <li>
            <strong>Trial that does not upgrade:</strong> read-only for 30 days after the trial
            ends, then permanently deleted.
          </li>
          <li>
            <strong>Cancelled paid account:</strong> retained for 30 days in a soft-locked state
            for billing-dispute resolution, then permanently deleted (see Section 8 — you can
            request earlier deletion at any time).
          </li>
          <li>
            <strong>Invoicing &amp; ledger records:</strong> retained for 7 years to comply with
            Irish Revenue record-keeping obligations (Taxes Consolidation Act 1997 s.886 and the
            VAT Consolidation Act 2010 s.84). On deletion of your account these records are
            anonymised (personal identifiers stripped) but the numeric tax data is kept.
          </li>
          <li>
            <strong>Failed-login records:</strong> IP-truncated logs are kept for 90 days for
            brute-force protection.
          </li>
          <li>
            <strong>Backup snapshots:</strong> encrypted rolling backups are retained for a
            maximum of 30 days.
          </li>
        </ul>
      </>
    ),
  },
  {
    n: 7,
    title: "Security measures",
    body: (
      <>
        <ul className="mt-3 space-y-2 list-disc pl-5 text-foreground/85">
          <li>TLS 1.2+ for every HTTP request; HSTS enforced on the production domain.</li>
          <li>Passwords and staff PINs hashed with bcrypt (cost ≥ 12).</li>
          <li>Database encryption at rest (AES-256, provider-managed keys).</li>
          <li>Tenant isolation enforced at the database driver level (every query is scoped by tenant id).</li>
          <li>Short-lived JWTs (24 hours) with server-side revocation on password / PIN change.</li>
          <li>Audit log of privileged actions (data exports, deletions, tenant merges).</li>
          <li>Principle of least privilege — production database access limited to on-call engineers with MFA.</li>
          <li>Regular dependency scanning and prompt patching of critical vulnerabilities.</li>
        </ul>
      </>
    ),
  },
  {
    n: 8,
    title: "Your rights under GDPR",
    body: (
      <>
        <p>You have the following rights, exercisable free of charge:</p>
        <ul className="mt-3 space-y-2 list-disc pl-5 text-foreground/85">
          <li><strong>Right of access</strong> (Art. 15) — obtain a copy of the personal data we hold about you.</li>
          <li><strong>Right to rectification</strong> (Art. 16) — correct inaccurate data. Managers can edit their profile in Settings.</li>
          <li><strong>Right to erasure</strong> (Art. 17, "right to be forgotten") — request deletion, subject to legal-retention exceptions in Section 6.</li>
          <li><strong>Right to data portability</strong> (Art. 20) — receive your data in a structured, commonly-used, machine-readable format (JSON). Available in Settings → Privacy &amp; data → Export my data.</li>
          <li><strong>Right to restriction of processing</strong> (Art. 18).</li>
          <li><strong>Right to object</strong> (Art. 21) — including a right to opt out of legitimate-interest processing at any time.</li>
          <li><strong>Right to withdraw consent</strong> (Art. 7(3)) — where processing is based on consent (marketing emails), you can withdraw at any time without affecting prior lawful processing.</li>
        </ul>
        <p className="mt-3">
          Most rights are self-serve via <em>Settings → Privacy &amp; data</em>. For anything
          else, email <a className="text-accent underline" href={`mailto:${COMPANY.contact_email}`}>{COMPANY.contact_email}</a>.
          We will respond within 30 days (extendable by up to 60 further days for complex
          requests, per Art. 12(3) GDPR).
        </p>
      </>
    ),
  },
  {
    n: 9,
    title: "Cookies and similar technologies",
    body: (
      <>
        <p>
          JobsBook.app uses only strictly-necessary cookies and browser storage — no analytics,
          no advertising, no cross-site tracking. Full details are in our{" "}
          <Link to="/cookies" className="text-accent underline">Cookie Policy</Link>.
        </p>
      </>
    ),
  },
  {
    n: 10,
    title: "Data-breach notification",
    body: (
      <>
        <p>
          If a personal-data breach is likely to result in a risk to the rights and freedoms of
          natural persons, we will notify the Irish Data Protection Commission within 72 hours of
          becoming aware of it, as required by Art. 33 GDPR. Where the breach is likely to
          result in a <em>high</em> risk to you, we will also notify you directly and without
          undue delay (Art. 34 GDPR), with a description of the nature of the breach, likely
          consequences and mitigation measures.
        </p>
      </>
    ),
  },
  {
    n: 11,
    title: "Complaints",
    body: (
      <>
        <p>
          You have the right to lodge a complaint with a supervisory authority, in particular the
          Irish Data Protection Commission (DPC):
        </p>
        <address className="not-italic mt-2 text-foreground/85 text-sm leading-relaxed">
          Data Protection Commission<br />
          21 Fitzwilliam Square South, Dublin 2, D02 RD28, Ireland<br />
          Web: <a className="text-accent underline" href={COMPANY.supervisory_url} target="_blank" rel="noopener noreferrer">{COMPANY.supervisory_url}</a>
        </address>
      </>
    ),
  },
  {
    n: 12,
    title: "Children",
    body: (
      <>
        <p>
          JobsBook.app is a B2B tool and is not directed at children under the age of 16. We do
          not knowingly collect personal data from children.
        </p>
      </>
    ),
  },
  {
    n: 13,
    title: "Changes to this policy",
    body: (
      <>
        <p>
          If we make material changes to this policy we will notify you by email and re-request
          your acknowledgement on next login. The version number and effective date at the top of
          this page always reflect the current policy.
        </p>
      </>
    ),
  },
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="privacy-page">
      <div className="border-b border-border">
        <div className="max-w-[820px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-bold" data-testid="privacy-back-home">
            <ArrowLeft className="h-4 w-4" /> Back to JobsBook
          </Link>
          <span className="mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Legal</span>
        </div>
      </div>

      <main className="max-w-[820px] mx-auto px-5 md:px-8 py-12">
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent">// {COMPANY.trading_name}</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Privacy Policy</h1>
        <div className="mt-2 text-sm text-muted-foreground">
          Effective: <span className="mono">{LEGAL_EFFECTIVE_DATE}</span>{" "}
          · Version <span className="mono">{LEGAL_VERSION}</span>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-foreground/80">
          This Privacy Policy explains how {COMPANY.legal_name} processes personal data when you
          use JobsBook.app. It is written to comply with Regulation (EU) 2016/679 (the "General
          Data Protection Regulation") and the Irish Data Protection Act 2018.
        </p>

        <div className="mt-8 space-y-8">
          {sections.map((s) => (
            <section key={s.n} data-testid={`privacy-section-${s.n}`}>
              <h2 className="text-lg font-extrabold tracking-tight">{s.n}. {s.title}</h2>
              <div className="mt-2 text-sm leading-relaxed text-foreground/85 space-y-2">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-border">
          <PublicFooter tone="dark" />
        </div>
      </main>
    </div>
  );
}
